import DetailPanel from 'Components/Generic/DetailPanel';
import DetailTable from 'Components/Generic/DetailPanel/DetailTable';
import UserNameWithDrawerButton from 'Components/SiteDrawerContainer/UserNameWithDrawerButton';
import { useAlertContext } from 'Data/context/Alerts';
import RequestData from 'Data/RequestData';
import { getStatusObject } from 'Pages/Requests/Enum';
import PropTypes from 'prop-types';
import { formatDate } from 'Util/dateUtil';
import { RequestStatusLabel } from './RequestStatus';

const getLatestPurchasedTransaction = (transactions) => {
  return transactions?.length &&
    transactions.filter((trans) => !!trans.date_purchased)?.length
    ? transactions
        .filter((trans) => !!trans.date_purchased)
        .reduce((a, b) => {
          return a.date_purchased > b.date_purchased ? a : b;
        })
    : {};
};

const getLatestReceivedLineItem = (lineItems) => {
  return lineItems?.length &&
    lineItems.filter((line) => !!line.date_received)?.length
    ? lineItems
        .filter((line) => !!line.date_received)
        .reduce((a, b) => {
          return a.date_received > b.date_received ? a : b;
        })
    : {};
};

const RequestWorkflowPanel = ({
  request = {},
  showProcessedLine = false,
  title = 'Workflow',
  editable = false,
  showAlternateFooter = false,
  alternateButtonFooter,
}) => {
  const { openConfirmationDialog } = useAlertContext();

  let approverId =
    request?.approver_id ||
    (request?.request_approval_requests?.length > 0 &&
      request.request_approval_requests.filter((rar) => !!rar.date_approved)
        ?.length)
      ? request.request_approval_requests.filter(
          (rar) => !!rar.date_approved,
        )[0].approver_id
      : null;
  let rejecterId =
    request?.rejecter_id ||
    (request?.request_approval_requests?.length > 0 &&
      request.request_approval_requests.filter((rar) => !!rar.date_rejected)
        ?.length)
      ? request.request_approval_requests.filter(
          (rar) => !!rar.date_rejected,
        )[0].approver_id
      : null;
  let purchaserId =
    request?.purchaser_id ||
    getLatestPurchasedTransaction(request.transactions)?.date_purchased ||
    null;
  let datePurchased =
    request?.date_purchased ||
    getLatestPurchasedTransaction(request.transactions)?.purchased_by ||
    request?.date_processed;
  let receiverId =
    request?.receiver_id ||
    getLatestReceivedLineItem(request.line_items)?.date_received ||
    null;
  let dateReceived =
    request?.date_received ||
    getLatestReceivedLineItem(request.line_items)?.received_by ||
    null;

  let fieldNames = ['statusType', 'userName', 'date'];
  let data = [
    {
      statusType: 'Requested',
      userName: (
        <UserNameWithDrawerButton userId={request.requesting_user_id} />
      ),
      date: formatDate(request.date_requested),
    },
    ...(request.request_approval_requests &&
    request.request_approval_requests?.length > 0 &&
    !request.date_rejected
      ? [
          {
            statusType: 'Approved',
            userName: <UserNameWithDrawerButton userId={approverId} />,
            date: formatDate(request.date_approved),
          },
        ]
      : []),
    ...(request.date_rejected
      ? [
          {
            statusType: 'Rejected',
            userName: <UserNameWithDrawerButton userId={rejecterId} />,
            date: formatDate(request.date_rejected),
          },
        ]
      : []),
    ...(showProcessedLine
      ? [
          {
            statusType: 'Processed',
            userName: (
              <UserNameWithDrawerButton userId={request.processing_user_id} />
            ),
            date: formatDate(request.date_processed),
          },
        ]
      : []),
    ...(!request.date_rejected
      ? [
          {
            statusType: 'Purchased',
            userName: <UserNameWithDrawerButton userId={purchaserId} />,
            date: formatDate(datePurchased),
          },
        ]
      : []),

    ...(!request.date_rejected
      ? [
          {
            statusType: 'Received',
            userName: <UserNameWithDrawerButton userId={receiverId} />,
            date: formatDate(dateReceived),
          },
        ]
      : []),
  ];

  const statusClicked = (nextStatus) => {
    openConfirmationDialog({
      onConfirm: () => updateStatus(nextStatus.key),
      dialogTitle: `Update status to '${nextStatus.label}'?`,
    });
  };
  const updateStatus = (newStatusKey) => {
    RequestData.updateStatus(request, newStatusKey);
  };

  let footer = (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
      }}>
      {showAlternateFooter && alternateButtonFooter ? (
        alternateButtonFooter
      ) : (
        <RequestStatusLabel
          selectedStatus={getStatusObject(request.status)}
          onSelectStatus={statusClicked}
          disabled={!editable}
        />
      )}
    </div>
  );

  return (
    <DetailPanel title={title} footer={footer}>
      <DetailTable fieldNames={fieldNames} data={data} verticalAlignCentered />
    </DetailPanel>
  );
};

RequestWorkflowPanel.propTypes = {
  request: PropTypes.object,
  showProcessedLine: PropTypes.bool,
  title: PropTypes.string,
  editable: PropTypes.bool,
  showAlternateFooter: PropTypes.bool,
  alternateButtonFooter: PropTypes.element,
};

export default RequestWorkflowPanel;
